interface VoucherConfig {
	restUrl: string;
	nonce: string;
	orderId: number;
	orderKey: string;
	pollIntervalMs: number;
}

interface VoucherStatusResponse {
	error: boolean;
	errorMsg: string;
	data: {
		status: "pending" | "ready" | "error";
		voucherHtml?: string;
		errorMessage?: string;
	};
}

declare const vpayVoucherCfg: VoucherConfig;

export class VoucherPoller {
	protected readonly $: JQueryStatic;
	protected config: VoucherConfig;
	protected intervalId: number = 0;

	constructor($: JQueryStatic) {
		this.$ = $;
		this.config = typeof vpayVoucherCfg !== "undefined" ? vpayVoucherCfg : null as unknown as VoucherConfig;

		this.$((): void => {
			this.init();
		});
	}

	protected init(): void {
		if (!this.config)
			return;

		const placeholder = this.$("#vpay-voucher-placeholder");
		if (placeholder.length === 0)
			return; // voucher already rendered server-side

		this.startPolling();
	}

	protected startPolling(): void {
		// poll immediately, then on interval
		this.poll();
		this.intervalId = window.setInterval((): void => {
			this.poll();
		}, this.config.pollIntervalMs);
	}

	protected stopPolling(): void {
		if (this.intervalId) {
			window.clearInterval(this.intervalId);
			this.intervalId = 0;
		}
	}

	protected poll(): void {
		const url = this.config.restUrl + "voucher-status/" + this.config.orderId
			+ "?order_key=" + encodeURIComponent(this.config.orderKey);

		this.$.ajax({
			url: url,
			method: "GET",
			beforeSend: (xhr: JQueryXHR): void => {
				xhr.setRequestHeader("X-WP-Nonce", this.config.nonce);
			},
			success: (response: VoucherStatusResponse): void => {
				this.handleResponse(response);
			},
			error: (): void => {
				// network error — keep polling, it may recover
			}
		});
	}

	protected handleResponse(response: VoucherStatusResponse): void {
		if (!response || !response.data)
			return;

		const data = response.data;

		if (data.status === "ready") {
			this.stopPolling();
			const placeholder = this.$("#vpay-voucher-placeholder");
			if (data.voucherHtml) {
				placeholder.replaceWith(data.voucherHtml);
			}
		}
		else if (data.status === "error") {
			this.stopPolling();
			const placeholder = this.$("#vpay-voucher-placeholder");
			placeholder.find(".vpay-generating").hide();
			const errorDiv = this.$("#vpay-voucher-error");
			errorDiv.find(".vpay-error-message").text(data.errorMessage || "Voucher generation failed.");
			errorDiv.show();
			this.bindRetryButton();
		}
		// status === "pending" — do nothing, keep polling
	}

	protected bindRetryButton(): void {
		const retryBtn = this.$("#vpay-voucher-retry");
		retryBtn.off("click.vpay").on("click.vpay", (): void => {
			this.retry();
		});
	}

	protected retry(): void {
		const url = this.config.restUrl + "voucher-retry/" + this.config.orderId
			+ "?order_key=" + encodeURIComponent(this.config.orderKey);

		// show spinner, hide error
		this.$("#vpay-voucher-error").hide();
		this.$("#vpay-voucher-placeholder .vpay-generating").show();

		this.$.ajax({
			url: url,
			method: "POST",
			beforeSend: (xhr: JQueryXHR): void => {
				xhr.setRequestHeader("X-WP-Nonce", this.config.nonce);
			},
			success: (): void => {
				this.startPolling();
			},
			error: (): void => {
				this.$("#vpay-voucher-placeholder .vpay-generating").hide();
				this.$("#vpay-voucher-error").show();
				this.$("#vpay-voucher-error .vpay-error-message").text("Failed to retry. Please refresh the page.");
			}
		});
	}
}